import React, { useEffect, useState } from "react";

const RASHIS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];
const RASHI_SHORT = ["Ari", "Tau", "Gem", "Can", "Leo", "Vir", "Lib", "Sco", "Sag", "Cap", "Aqu", "Pis"];
const PLANET_ABBR = {
  Sun: "Su", Moon: "Mo", Mars: "Ma", Mercury: "Me", Jupiter: "Ju",
  Venus: "Ve", Saturn: "Sa", Rahu: "Ra", Ketu: "Ke",
};

// ─── Global chart-style preference (persisted + synced across all charts) ───
const CHART_STYLE_KEY = "chart_style_pref";
const CHART_STYLE_EVENT = "chart-style-change";

function readChartStylePref() {
  if (typeof window === "undefined") return "north";
  try {
    const v = window.localStorage.getItem(CHART_STYLE_KEY);
    return v === "south" ? "south" : "north";
  } catch {
    return "north";
  }
}

export function setChartStylePref(style) {
  if (typeof window === "undefined") return;
  const val = style === "south" ? "south" : "north";
  try {
    window.localStorage.setItem(CHART_STYLE_KEY, val);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(CHART_STYLE_EVENT, { detail: val }));
}

export function useChartStyle() {
  const [style, setStyle] = useState(() => readChartStylePref());
  useEffect(() => {
    const handler = (e) => setStyle(e.detail === "south" ? "south" : "north");
    window.addEventListener(CHART_STYLE_EVENT, handler);
    return () => window.removeEventListener(CHART_STYLE_EVENT, handler);
  }, []);
  return style;
}

// Small inline pill toggle rendered above a chart. Writing the pref
// broadcasts a global event so every mounted chart re-renders instantly.
export function ChartStyleToggle({ className = "" }) {
  const style = useChartStyle();
  const btn = (val, label) => (
    <button
      type="button"
      onClick={() => setChartStylePref(val)}
      data-testid={`chart-style-toggle-${val}`}
      aria-pressed={style === val}
      className={
        "px-2.5 py-1 text-[11px] font-semibold tracking-wide rounded-full transition-colors " +
        (style === val
          ? "bg-[#7C2D12] text-white shadow-sm"
          : "text-[#7C2D12] hover:bg-amber-100")
      }
    >
      {label}
    </button>
  );
  return (
    <div
      data-testid="chart-style-toggle"
      className={
        "inline-flex items-center gap-0.5 p-0.5 rounded-full border border-amber-300 bg-amber-50/70 " +
        className
      }
    >
      {btn("north", "North")}
      {btn("south", "South")}
    </div>
  );
}

// ─── North-Indian layout (300x300 viewBox) ───
// Centroids for each of the 12 houses. House 1 (Lagna) is the top rhombus.
const HOUSE_CENTROIDS_NORTH = {
  1:  { x: 150, y: 75 },
  2:  { x: 75,  y: 27 },
  3:  { x: 27,  y: 75 },
  4:  { x: 75,  y: 150 },
  5:  { x: 27,  y: 225 },
  6:  { x: 75,  y: 273 },
  7:  { x: 150, y: 225 },
  8:  { x: 225, y: 273 },
  9:  { x: 273, y: 225 },
  10: { x: 225, y: 150 },
  11: { x: 273, y: 75 },
  12: { x: 225, y: 27 },
};

// ─── South-Indian layout (300x300 viewBox) ───
// 4×4 grid with fixed sign positions; center 2×2 is a single blank block.
// Cell size is 75. Sign index → centroid (using 1=Aries … 12=Pisces).
const SIGN_CENTROIDS_SOUTH = {
  12: { x: 37.5,  y: 37.5,  col: 0, row: 0 }, // Pisces
  1:  { x: 112.5, y: 37.5,  col: 1, row: 0 }, // Aries
  2:  { x: 187.5, y: 37.5,  col: 2, row: 0 }, // Taurus
  3:  { x: 262.5, y: 37.5,  col: 3, row: 0 }, // Gemini
  4:  { x: 262.5, y: 112.5, col: 3, row: 1 }, // Cancer
  5:  { x: 262.5, y: 187.5, col: 3, row: 2 }, // Leo
  6:  { x: 262.5, y: 262.5, col: 3, row: 3 }, // Virgo
  7:  { x: 187.5, y: 262.5, col: 2, row: 3 }, // Libra
  8:  { x: 112.5, y: 262.5, col: 1, row: 3 }, // Scorpio
  9:  { x: 37.5,  y: 262.5, col: 0, row: 3 }, // Sagittarius
  10: { x: 37.5,  y: 187.5, col: 0, row: 2 }, // Capricorn
  11: { x: 37.5,  y: 112.5, col: 0, row: 1 }, // Aquarius
};

const STROKE = "rgba(217, 119, 6, 0.45)";
const GOLD = "#FFB800";
const TEXT = "#1A1410";

function renderNorth({ kundali, kpMode, houseOverrides, houseSigns, label, size }) {
  const lagnaIdx = kundali?.lagna?.rashi ? RASHIS.indexOf(kundali.lagna.rashi) : 0;
  const housePlanets = {};
  for (let h = 1; h <= 12; h++) housePlanets[h] = [];
  if (kpMode) {
    for (const [planet, houseNum] of Object.entries(houseOverrides)) {
      if (!houseNum || houseNum < 1 || houseNum > 12) continue;
      housePlanets[houseNum].push({
        abbr: PLANET_ABBR[planet] || planet.slice(0, 2),
        retro: !!(kundali?.planets?.[planet]?.retrograde),
      });
    }
  } else {
    for (const [planet, info] of Object.entries(kundali.planets)) {
      const idx = RASHIS.indexOf(info.rashi);
      if (idx < 0) continue;
      const houseNum = ((idx - lagnaIdx + 12) % 12) + 1;
      housePlanets[houseNum].push({
        abbr: PLANET_ABBR[planet] || planet.slice(0, 2),
        retro: !!info.retrograde,
      });
    }
  }

  return (
    <svg
      viewBox="0 0 300 300"
      width={size}
      height={size}
      data-testid={kpMode ? "kp-chart" : "kundali-chart"}
      data-chart-style="north"
      style={{ background: "#FFFFFF" }}
    >
      <rect x="1" y="1" width="298" height="298" fill="none" stroke={STROKE} strokeWidth="1.5" />
      <line x1="0" y1="0" x2="300" y2="300" stroke={STROKE} strokeWidth="1.2" />
      <line x1="300" y1="0" x2="0" y2="300" stroke={STROKE} strokeWidth="1.2" />
      <polygon points="150,0 300,150 150,300 0,150" fill="none" stroke={STROKE} strokeWidth="1.2" />

      {label && (
        <text x={150} y={150} fill={GOLD} fontSize="10" fontWeight="600" letterSpacing="1.5"
              textAnchor="middle" fontFamily="Outfit, sans-serif" opacity="0.5">
          {label}
        </text>
      )}

      {Object.entries(HOUSE_CENTROIDS_NORTH).map(([h, { x, y }]) => {
        const hNum = parseInt(h);
        let signNum;
        if (kpMode) {
          if (houseSigns && houseSigns[hNum] != null) {
            const sv = houseSigns[hNum];
            signNum = typeof sv === "number"
              ? ((sv % 12) + 12) % 12 + 1
              : (RASHIS.indexOf(sv) >= 0 ? RASHIS.indexOf(sv) + 1 : hNum);
          } else {
            signNum = hNum;
          }
        } else {
          signNum = ((lagnaIdx + hNum - 1) % 12) + 1;
        }
        const planets = housePlanets[hNum];
        const isLagna = hNum === 1;

        const lines = [];
        if (isLagna) lines.push({ text: "Lg", color: GOLD, weight: 600, size: 10 });
        planets.forEach((p) => {
          lines.push({
            text: `${p.abbr}${p.retro ? "ᴿ" : ""}`,
            color: TEXT,
            weight: 500,
            size: 11,
          });
        });
        const LH = 12;
        const totalH = lines.length * LH;
        const startY = y - totalH / 2 + 8;

        return (
          <g key={h}>
            <text
              x={x}
              y={y - totalH / 2}
              fill={GOLD}
              fontSize="9"
              fontWeight="500"
              textAnchor="middle"
              opacity="0.75"
              fontFamily="Outfit, sans-serif"
            >
              {signNum}
            </text>
            {lines.map((line, i) => (
              <text
                key={i}
                x={x}
                y={startY + i * LH}
                fill={line.color}
                fontSize={line.size}
                fontWeight={line.weight}
                textAnchor="middle"
                fontFamily="Outfit, sans-serif"
              >
                {line.text}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function renderSouth({ kundali, label, size }) {
  const lagnaIdx = RASHIS.indexOf(kundali.lagna.rashi); // 0-based sign index
  const lagnaSignNum = lagnaIdx + 1; // 1..12

  // Bucket planets by their actual rashi (sign) for South-Indian layout.
  const signPlanets = {};
  for (let s = 1; s <= 12; s++) signPlanets[s] = [];
  for (const [planet, info] of Object.entries(kundali.planets)) {
    const idx = RASHIS.indexOf(info.rashi);
    if (idx < 0) continue;
    signPlanets[idx + 1].push({
      abbr: PLANET_ABBR[planet] || planet.slice(0, 2),
      retro: !!info.retrograde,
    });
  }

  return (
    <svg
      viewBox="0 0 300 300"
      width={size}
      height={size}
      data-testid="kundali-chart"
      data-chart-style="south"
      style={{ background: "#FFFFFF" }}
    >
      {/* Outer frame */}
      <rect x="1" y="1" width="298" height="298" fill="none" stroke={STROKE} strokeWidth="1.5" />
      {/* Full-length inner grid lines */}
      <line x1="75"  y1="0"   x2="75"  y2="300" stroke={STROKE} strokeWidth="1.2" />
      <line x1="225" y1="0"   x2="225" y2="300" stroke={STROKE} strokeWidth="1.2" />
      <line x1="0"   y1="75"  x2="300" y2="75"  stroke={STROKE} strokeWidth="1.2" />
      <line x1="0"   y1="225" x2="300" y2="225" stroke={STROKE} strokeWidth="1.2" />
      {/* Partial lines outside the center 2×2 block */}
      <line x1="150" y1="0"   x2="150" y2="75"  stroke={STROKE} strokeWidth="1.2" />
      <line x1="150" y1="225" x2="150" y2="300" stroke={STROKE} strokeWidth="1.2" />
      <line x1="0"   y1="150" x2="75"  y2="150" stroke={STROKE} strokeWidth="1.2" />
      <line x1="225" y1="150" x2="300" y2="150" stroke={STROKE} strokeWidth="1.2" />

      {label && (
        <text x={150} y={155} fill={GOLD} fontSize="11" fontWeight="700" letterSpacing="1.8"
              textAnchor="middle" fontFamily="Outfit, sans-serif" opacity="0.65">
          {label}
        </text>
      )}

      {Object.entries(SIGN_CENTROIDS_SOUTH).map(([sNum, { x, y }]) => {
        const signNum = parseInt(sNum);
        const isLagna = signNum === lagnaSignNum;
        // House number from lagna, clockwise (which matches sign order in South layout)
        const houseNum = ((signNum - lagnaSignNum + 12) % 12) + 1;
        const planets = signPlanets[signNum];

        const lines = [];
        if (isLagna) lines.push({ text: "Lg", color: GOLD, weight: 700, size: 10 });
        planets.forEach((p) => {
          lines.push({
            text: `${p.abbr}${p.retro ? "ᴿ" : ""}`,
            color: TEXT,
            weight: 500,
            size: 11,
          });
        });
        const LH = 12;
        const totalH = lines.length * LH;
        const startY = y - totalH / 2 + 12;

        return (
          <g key={sNum}>
            {/* Lagna cell highlight — subtle diagonal marker */}
            {isLagna && (
              <line
                x1={x - 30}
                y1={y - 30}
                x2={x - 15}
                y2={y - 15}
                stroke={GOLD}
                strokeWidth="1.5"
              />
            )}
            {/* Small sign short-name in top-left of the cell */}
            <text
              x={x - 28}
              y={y - 22}
              fill={GOLD}
              fontSize="8.5"
              fontWeight="600"
              textAnchor="start"
              opacity="0.85"
              fontFamily="Outfit, sans-serif"
            >
              {RASHI_SHORT[signNum - 1]}
            </text>
            {/* House number in top-right */}
            <text
              x={x + 28}
              y={y - 22}
              fill={GOLD}
              fontSize="8.5"
              fontWeight="500"
              textAnchor="end"
              opacity="0.6"
              fontFamily="Outfit, sans-serif"
            >
              {houseNum}
            </text>
            {lines.map((line, i) => (
              <text
                key={i}
                x={x}
                y={startY + i * LH}
                fill={line.color}
                fontSize={line.size}
                fontWeight={line.weight}
                textAnchor="middle"
                fontFamily="Outfit, sans-serif"
              >
                {line.text}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

export default function KundaliChart({
  kundali,
  size = 340,
  houseOverrides,
  houseSigns,
  label,
  style,        // optional override: "north" | "south"
  showToggle = true,
}) {
  const globalStyle = useChartStyle();
  const kpMode = !!houseOverrides;
  // KP / Bhav-Chalit is house-based; South-Indian sign layout doesn't apply cleanly.
  const effectiveStyle = kpMode ? "north" : (style || globalStyle);

  if (!kpMode && (!kundali?.lagna?.rashi || !kundali?.planets)) return null;
  const lagnaIdx = kundali?.lagna?.rashi ? RASHIS.indexOf(kundali.lagna.rashi) : 0;
  if (!kpMode && lagnaIdx < 0) return null;

  const chartSvg = effectiveStyle === "south"
    ? renderSouth({ kundali, label, size })
    : renderNorth({ kundali, kpMode, houseOverrides, houseSigns, label, size });

  if (!showToggle || kpMode) {
    return chartSvg;
  }

  return (
    <div
      className="inline-flex flex-col items-center"
      data-testid="kundali-chart-wrap"
      data-chart-style={effectiveStyle}
    >
      <div className="mb-2">
        <ChartStyleToggle />
      </div>
      {chartSvg}
    </div>
  );
}
